import { FunctionComponent, useEffect, useMemo, useRef, useState } from "react";
import { useTranslation } from "react-i18next";

import { format } from "date-fns";
import { useNavigate, useSearchParams } from "react-router-dom";

import { Button } from "@/components/Button/Button";
import { ROUTE_CONFIG } from "@/routes/config";
import { ButtonColor } from "@/components/Button/types";
import { useGetCreditsQuery } from "@/pages/Private/redux/usage/usage.api";
import { LoadingOverlay } from "@/components/LoadingOverlay/LoadingOverlay";
import {
	useLazyCreatePortalSessionQuery,
	useGetPaymentMethodQuery,
	useSkipTrialMutation,
	useCancelSubscriptionMutation,
	useFinalizeCreditsPurchaseMutation,
	useReactivateSubscriptionMutation,
} from "@/pages/Private/redux/subscription/subscription.api";
import { useSnackBar } from "@/utils/useSnackbar";
import { Accordion, Modal, TopupCreditsModal } from "@/components";
import { authSelector } from "@/pages/Public/redux/auth.slice";
import { useAppSelector } from "@/redux/hooks";
import { useSlotsAvailableQuery } from "@/pages/Private/redux/unipile/unipile.api";
import { LinkedinIcon } from "@/components/LinkedinIcon/LinkedinIcon";
import { EmailIcon } from "@/components/EmailIcon/EmailIcon";
import { useGetWebsiteScrapeCreditsQuery } from "@/pages/Private/redux/websiteScrape/websiteScrape.api";
import { SelectWebsiteScrapeProduct } from "@/components/SelectWebsiteScrapeProduct/SelectWebsiteScrapeProduct";
import { EditEmailModal } from "@/pages/Private/pages/Users/components/EditEmailModal";
import { EditLinkedinModal } from "@/pages/Private/pages/Users/components/EditLinkedinModal";

import { billingFaqItems } from "../components/billingFaqItems";
import {
	CancelSubscriptionModal,
	ExpectedCancellationEnd,
} from "../components/CancelSubscriptionModal";
import { ChangeSubscriptionModal } from "../components/ChangeSubscriptionModal";
import { PendingPlanChangeNotice } from "../components/PendingPlanChangeNotice";
import { getDiscountInfo } from "../utils/discount-info.utils";

import { SenderSeatCard } from "../components/SenderSeatCard";
import { classifyFinalizeError } from "./utils/finalize-credits-purchase.utils";
import { useCancellationNotice } from "./utils/useCancellationNotice";

export const UsageOverview: FunctionComponent = () => {
	const { t } = useTranslation();
	const navigate = useNavigate();
	const [searchParams, setSearchParams] = useSearchParams();
	const { data, isLoading, refetch: refetchCredits } = useGetCreditsQuery();
	const { user } = useAppSelector(authSelector);
	const [finalizeCreditsPurchase] = useFinalizeCreditsPurchaseMutation();
	const { handleOpenSnackbar, renderMessageSnackbar } = useSnackBar();
	const hasFinalizedRef = useRef(false);
	const [isPendingPaymentOpen, setIsPendingPaymentOpen] = useState(false);

	// Finalize credit purchase after Stripe redirect
	useEffect(() => {
		const success = searchParams.get("success");
		const sessionId = searchParams.get("session_id");

		if (success !== "true" || !sessionId || hasFinalizedRef.current) {
			return;
		}

		hasFinalizedRef.current = true;

		const finalize = async () => {
			try {
				const result = await finalizeCreditsPurchase({ sessionId }).unwrap();

				// SEPA Direct Debit is granted while still "processing": the credits are usable
				// immediately even though the payment has not settled yet.
				handleOpenSnackbar(
					result.paymentPending
						? "Credits are available now — your SEPA payment is still being processed."
						: "Credits have been added to your account"
				);
			} catch (err: unknown) {
				const kind = classifyFinalizeError(err);
				const message = (err as { data?: { message?: string } })?.data?.message;

				if (kind === "pending") {
					// Async payment method (SEPA DD / bank transfer) — credits will be granted
					// automatically by the backend once the payment settles.
					setIsPendingPaymentOpen(true);
				} else if (kind === "forbidden") {
					handleOpenSnackbar(message || "You are not authorized to finalize this purchase.");
				} else {
					handleOpenSnackbar(
						message || "Could not finalize credit purchase. Please contact support."
					);
				}
			}

			// Clean up URL params
			setSearchParams(
				(prev) => {
					const next = new URLSearchParams(prev);

					next.delete("success");
					next.delete("session_id");

					return next;
				},
				{ replace: true }
			);
		};

		finalize();
	}, []);
	const { data: slotsData, refetch: refetchSlots } = useSlotsAvailableQuery(user?.company?.id);
	const { data: websiteScrapeData } = useGetWebsiteScrapeCreditsQuery();
	const { data: paymentMethodData, isError: isPaymentMethodError } = useGetPaymentMethodQuery();

	const [createPortalSession, { isLoading: isCreatingPortalSession }] =
		useLazyCreatePortalSessionQuery();
	const [skipTrial, { isLoading: isSkippingTrial }] = useSkipTrialMutation();

	// Check if user has a subscription
	const hasSubscription = useMemo(() => {
		return !!data?.stripeSubscription;
	}, [data]);

	const [showWebsiteScrapeModal, setShowWebsiteScrapeModal] = useState(false);
	const [showTopupCreditsModal, setShowTopupCreditsModal] = useState(false);
	const [openFaqItemId, setOpenFaqItemId] = useState<string | null>(null);
	const [showEditEmailModal, setShowEditEmailModal] = useState(false);
	const [showEditLinkedinModal, setShowEditLinkedinModal] = useState(false);
	const [showChangeSubscriptionModal, setShowChangeSubscriptionModal] = useState(false);
	const [showSkipTrialModal, setShowSkipTrialModal] = useState(false);
	const [showCancelModal, setShowCancelModal] = useState(false);
	const [cancelError, setCancelError] = useState<string | null>(null);
	const [showReactivateModal, setShowReactivateModal] = useState(false);
	const [reactivateError, setReactivateError] = useState<string | null>(null);

	const [cancelSubscription, { isLoading: isCancelling }] = useCancelSubscriptionMutation();
	const [reactivateSubscription, { isLoading: isReactivating }] =
		useReactivateSubscriptionMutation();

	// Calculate next billing date - prefer upcomingInvoice from Stripe, fallback to nextCycle
	const nextBillingDate = useMemo(() => {
		if (data?.upcomingInvoice?.next_payment_attempt) {
			return new Date(data.upcomingInvoice.next_payment_attempt * 1000).toLocaleDateString(
				"de-DE",
				{
					day: "2-digit",
					month: "2-digit",
					year: "numeric",
				}
			);
		}

		return data?.nextCycle
			? new Date(data?.nextCycle).toLocaleDateString("de-DE", {
					day: "2-digit",
					month: "2-digit",
					year: "numeric",
				})
			: "";
	}, [data]);

	// Calculate credits left - use data.credits which already includes rollover
	const creditsLeft = useMemo(() => {
		return data?.credits ?? (data?.cycleCredits || 0) - (data?.usedCredits || 0);
	}, [data]);

	// Determine plan name and interval
	const planInfo = useMemo(() => {
		const perMonthOrYear = data?.perMonthOrYear;
		const productName = data?.stripeProduct?.name || "Starter plan";
		// Check perMonthOrYear first, fallback to stripePrice recurring data
		const isSemiAnnual =
			perMonthOrYear === "HALF_YEAR" ||
			(data?.stripePrice?.recurring?.interval === "month" &&
				data?.stripePrice?.recurring?.interval_count === 6);
		const isQuarterly =
			!isSemiAnnual &&
			(perMonthOrYear === "QUARTER" ||
				(data?.stripePrice?.recurring?.interval === "month" &&
					data?.stripePrice?.recurring?.interval_count === 3));
		const isYearly = !isSemiAnnual && !isQuarterly && perMonthOrYear === "YEAR";

		const interval = isSemiAnnual
			? "semi-annual"
			: isQuarterly
				? "quarterly"
				: isYearly
					? "yearly"
					: "monthly";

		return {
			name: productName,
			interval,
			creditsPerPeriod:
				isYearly || isSemiAnnual || isQuarterly
					? (data?.stripeProduct?.metadata?.credits || 0) * 12
					: data?.stripeProduct?.metadata?.credits || data?.cycleCredits || 0,
		};
	}, [data]);

	// Compute discount info from the Stripe subscription
	const discountInfo = useMemo(() => getDiscountInfo(data?.stripeSubscription), [data]);

	// Calculate sender accounts used
	const senderAccountsInfo = useMemo(() => {
		const linkedinUsed = slotsData?.slotsAvailable ? slotsData.slotsAvailable : 0;
		const emailsUsed = slotsData?.emailsUsed || 0;
		const totalUsed = linkedinUsed + emailsUsed;
		const totalAvailable =
			(slotsData?.slotAmount || 0) + (slotsData?.emailsAmount || slotsData?.emailBaseAmount || 0);

		return {
			used: totalUsed,
			total: totalAvailable,
			linkedinUsed,
			emailsUsed,
		};
	}, [slotsData]);

	const handleOpenStripePortal = async () => {
		const result = await createPortalSession().unwrap();

		if (result.sessionUrl) {
			window.open(result.sessionUrl, "_blank");
		}
	};

	// Check if user is currently in trial
	const isTrialing = useMemo(() => {
		return data?.stripeSubscription?.status === "trialing";
	}, [data]);

	// Check if subscription is canceled and determine the reason for the end date
	// A subscription is "pending cancellation" when Stripe reports cancel_at_period_end, a future
	// cancel_at, or a canceled_at. canceled_at alone is not enough: Stripe omits it for future-dated
	// cancel_at (installment/discount cancellations), which would otherwise hide the whole notice.
	const cancellationNotice = useCancellationNotice(data?.stripeSubscription, data?.cancellation);

	// Compute expected cancellation end date for the cancel confirmation modal (pre-cancellation).
	// While the subscription trials, the current period ends at trial_end — so this is the date the
	// customer keeps access to, with no charge (PREC-3757).
	const expectedCancellationEndInfo = useMemo<ExpectedCancellationEnd | null>(() => {
		const sub = data?.stripeSubscription;

		if (!sub || sub.canceled_at) {
			return null;
		}

		const currentPeriodEnd = sub.items?.data?.[0]?.current_period_end
			? new Date(sub.items.data[0].current_period_end * 1000)
			: null;

		const formatDate = (date: Date) =>
			date.toLocaleDateString("de-DE", {
				day: "2-digit",
				month: "2-digit",
				year: "numeric",
			});

		return {
			currentPeriodEndFormatted: currentPeriodEnd ? formatDate(currentPeriodEnd) : null,
			hasDiscount: !!discountInfo,
			discountLabel: discountInfo?.label || null,
			discountEndFormatted: discountInfo?.endDateFormatted || null,
		};
	}, [data, discountInfo]);

	const handleSkipTrial = async () => {
		await skipTrial().unwrap();
		setShowSkipTrialModal(false);

		// Poll up to 5 times to refetch data until user is no longer in trial
		const maxRetries = 5;
		const delayMs = 1000;

		for (let attempt = 0; attempt < maxRetries; attempt++) {
			await new Promise((resolve) => setTimeout(resolve, delayMs));

			const creditsResult = await refetchCredits().unwrap();

			await refetchSlots();

			// Stop polling if user is no longer in trial
			if (creditsResult?.stripeSubscription?.status !== "trialing") {
				break;
			}
		}
	};

	const handleCancelSubscription = async () => {
		const subscriptionId = data?.stripeSubscription?.id;

		if (!subscriptionId) {
			return;
		}

		setCancelError(null);

		try {
			await cancelSubscription({ subscriptionId }).unwrap();
			setShowCancelModal(false);
			refetchCredits();
		} catch (err: unknown) {
			const error = err as { status?: number; data?: { message?: string } };

			if (error?.status === 403) {
				setCancelError(
					error?.data?.message || t("usage.subscription.overview.installmentCommitmentError")
				);
			} else {
				setCancelError(t("usage.subscription.overview.cancelFailed"));
			}
		}
	};

	const handleReactivateSubscription = async () => {
		const subscriptionId = data?.stripeSubscription?.id;

		if (!subscriptionId) {
			return;
		}

		setReactivateError(null);

		try {
			await reactivateSubscription({ subscriptionId }).unwrap();
			setShowReactivateModal(false);
			handleOpenSnackbar(t("usage.subscription.overview.reactivatedSuccess"));
			refetchCredits();
		} catch (err: unknown) {
			const error = err as { status?: number; data?: { message?: string } };

			setReactivateError(error?.data?.message || t("usage.subscription.overview.reactivateFailed"));
		}
	};

	return isLoading ? (
		<LoadingOverlay />
	) : (
		<div className="grid grid-cols-1 lg:grid-cols-2 gap-8 w-full">
			{/* Left Column - Main Content */}
			<div className="flex flex-col gap-4">
				{/* Header */}
				<div className="text-sm text-gray-600 mb-2">
					Manage your plan, billing information and invoices. For questions, please contact{" "}
					<a className="text-brand-dark underline" href="mailto:support@signalist.io">
						support@signalist.io
					</a>
				</div>

				{/* Current Plan Section */}
				<div className="flex flex-col rounded-xl border border-gray-200 bg-brand-white p-5">
					<div className="flex flex-row items-center gap-2 mb-3">
						<div className="text-sm font-semibold">
							{t("usage.subscription.overview.currentPlan", {
								name: planInfo.name,
								interval: planInfo.interval,
							})}
						</div>
						{isTrialing && (
							<span className="text-xs bg-blue-100 text-blue-800 px-2 py-0.5 rounded-full">
								{t("usage.subscription.overview.trial")}
							</span>
						)}
						{discountInfo && (
							<span className="text-xs bg-green-100 text-green-800 px-2 py-0.5 rounded-full">
								{discountInfo.label}
								{discountInfo.endDateFormatted && ` · until ${discountInfo.endDateFormatted}`}
							</span>
						)}
					</div>
					<div className="text-sm text-gray-600 mb-1">
						{planInfo.interval === "semi-annual"
							? t("usage.subscription.overview.creditsPerYearSemi", {
									credits: planInfo.creditsPerPeriod.toLocaleString("de-DE"),
								})
							: planInfo.interval === "quarterly"
								? t("usage.subscription.overview.creditsPerYearQuarter", {
										credits: planInfo.creditsPerPeriod.toLocaleString("de-DE"),
									})
								: planInfo.interval === "yearly"
									? t("usage.subscription.overview.creditsPerYear", {
											credits: planInfo.creditsPerPeriod.toLocaleString("de-DE"),
										})
									: t("usage.subscription.overview.creditsPerMonth", {
											credits: planInfo.creditsPerPeriod.toLocaleString("de-DE"),
										})}
					</div>
					{isTrialing && (
						<div className="text-sm text-amber-700 bg-amber-50 border border-amber-200 rounded-md p-3 mb-3">
							{t("usage.subscription.overview.trialNotice")}
						</div>
					)}
					{cancellationNotice && (
						<div className="text-sm text-red-700 bg-red-50 border border-red-200 rounded-md p-3 mb-3">
							{t(cancellationNotice.key, cancellationNotice.params)}
						</div>
					)}
					<PendingPlanChangeNotice pendingPlanChange={data?.pendingPlanChange} />
					{nextBillingDate && (
						<div className="text-sm text-gray-500 mb-4">
							{t("usage.subscription.overview.nextBillingDate", { date: nextBillingDate })}
						</div>
					)}
					<div className="mt-2 flex flex-row gap-2">
						{hasSubscription ? (
							<Button
								color={ButtonColor.ACTION_SECONDARY}
								title={t("usage.subscription.overview.changePlan")}
								onClick={() => setShowChangeSubscriptionModal(true)}
							/>
						) : (
							<Button
								color={ButtonColor.ACTION}
								title={t("usage.subscription.overview.selectPlan")}
								onClick={() => navigate(ROUTE_CONFIG.SELECT_SUBSCRIPTION)}
							/>
						)}
						{isTrialing && (
							<Button
								color={ButtonColor.ACTION}
								disabled={isSkippingTrial}
								title={
									isSkippingTrial
										? t("usage.subscription.overview.skipping")
										: t("usage.subscription.overview.skipTrial")
								}
								onClick={() => setShowSkipTrialModal(true)}
							/>
						)}
						{/* Also offered during the trial: hiding it left trial customers with no
						    self-serve way to cancel at all (PREC-3757). */}
						{hasSubscription && !cancellationNotice && (
							<Button
								color={ButtonColor.ACTION_SECONDARY}
								disabled={isCancelling}
								title={t("usage.subscription.overview.cancelSubscription")}
								onClick={() => {
									setCancelError(null);
									setShowCancelModal(true);
								}}
							/>
						)}
						{hasSubscription && cancellationNotice && (
							<Button
								color={ButtonColor.ACTION}
								disabled={isReactivating}
								title={t("usage.subscription.overview.reactivateSubscription")}
								onClick={() => {
									setReactivateError(null);
									setShowReactivateModal(true);
								}}
							/>
						)}
					</div>
					{cancelError && (
						<div className="text-sm text-red-700 bg-red-50 border border-red-200 rounded-md p-3 mt-3">
							{cancelError}
						</div>
					)}
				</div>

				{/* Credit Usage Section */}
				<div className="flex flex-col rounded-xl border border-gray-200 bg-brand-white p-5">
					<div className="text-sm font-semibold mb-4">Credit usage</div>
					<div className="flex flex-row gap-8 mb-4">
						<div className="flex flex-col">
							<div className="text-2xl font-bold">{creditsLeft.toLocaleString("de-DE")}</div>
							<div className="text-sm text-gray-500">credits left</div>
						</div>
						<div className="flex flex-col">
							<div className="text-2xl font-bold">
								{(websiteScrapeData?.credits || 0).toLocaleString("de-DE")}
							</div>
							<div className="text-sm text-gray-500">Webscrape Job credits left</div>
						</div>
					</div>
					<div className="text-sm text-gray-600 mb-1">
						Credits will refill{" "}
						{data?.upcomingInvoice?.next_payment_attempt
							? format(new Date(data.upcomingInvoice.next_payment_attempt * 1000), "MMMM do, yyyy")
							: data?.nextCycle
								? format(new Date(data.nextCycle), "MMMM do, yyyy")
								: ""}
					</div>
					{data?.rolloverCredits && data.rolloverCredits > 0 ? (
						<>
							<div className="text-sm text-gray-600 mb-3">
								{data.rolloverCredits} credits will roll over from this month
							</div>
							<button
								className="text-sm text-brand-dark underline cursor-pointer mb-4 text-left"
								type="button"
								onClick={() => setOpenFaqItemId("rollover-credits")}
							>
								How is it calculated?
							</button>
						</>
					) : (
						""
					)}

					<div className="mt-2 flex flex-row gap-2">
						<Button
							color={ButtonColor.ACTION_SECONDARY}
							disabled={!data?.stripeSubscription}
							title="Add more credits"
							onClick={() => setShowTopupCreditsModal(true)}
						/>
						<Button
							color={ButtonColor.ACTION_SECONDARY}
							disabled={!data?.stripeSubscription}
							title="Add Webscrape credits"
							onClick={() => setShowWebsiteScrapeModal(true)}
						/>
					</div>
				</div>

				{/* LinkedIn seat add-on (prod: "additional LinkedIn sender") */}
				<SenderSeatCard
					baseAmount={slotsData?.linkedinBaseAmount ?? 0}
					bonusAmount={slotsData?.linkedinsBonusAmount ?? 0}
					buttonDisabled={!data?.stripeSubscription}
					buttonTitle="Add more LinkedIn sender seats"
					connectedRow={
						(slotsData?.linkedinsUsed || 0) > 0 ? (
							<div className="flex items-center gap-2">
								<LinkedinIcon />
								<span>{slotsData?.linkedinsUsed || 0} LinkedIn sender(s) connected</span>
							</div>
						) : (
							<div className="text-gray-500">No LinkedIn senders connected yet</div>
						)
					}
					pendingAmount={slotsData?.linkedinsPendingAmount}
					pendingEffectiveAt={slotsData?.linkedinsPendingEffectiveAt}
					title="LinkedIn Sender Seats"
					total={slotsData?.linkedinsAmount || 0}
					used={slotsData?.linkedinsUsed || 0}
					usedLabel="LinkedIn sender seats used"
					onButtonClick={() => setShowEditLinkedinModal(true)}
				/>

				{/* Email seat add-on */}
				<SenderSeatCard
					baseAmount={slotsData?.emailBaseAmount ?? 0}
					bonusAmount={slotsData?.emailsBonusAmount ?? 0}
					buttonDisabled={!data?.stripeSubscription}
					buttonTitle="Add more email sending accounts"
					connectedRow={
						senderAccountsInfo.emailsUsed > 0 ? (
							<div className="flex items-center gap-2">
								<EmailIcon />
								<span>{senderAccountsInfo.emailsUsed} email address(es) connected</span>
							</div>
						) : (
							<div className="text-gray-500">No email addresses connected yet</div>
						)
					}
					pendingAmount={slotsData?.emailsPendingAmount}
					pendingEffectiveAt={slotsData?.emailsPendingEffectiveAt}
					title="Email Sender Accounts"
					total={slotsData?.emailsAmount || 0}
					used={senderAccountsInfo.emailsUsed}
					usedLabel="email addresses used"
					onButtonClick={() => setShowEditEmailModal(true)}
				/>

				{/* Payment Method Section */}
				<div className="flex flex-col rounded-xl border border-gray-200 bg-brand-white p-5">
					<div className="text-sm font-semibold mb-3">Payment method</div>
					<div className="flex flex-col gap-2 text-sm text-gray-600 mb-4">
						<div>
							<span className="text-gray-500">Send invoices to: </span>
							<span>{user?.email || "—"}</span>
						</div>
						<div>
							<span className="text-gray-500">Payment info: </span>
							{paymentMethodData?.type === "card" && paymentMethodData.last4 ? (
								<span>
									{paymentMethodData.brand
										? paymentMethodData.brand.charAt(0).toUpperCase() +
											paymentMethodData.brand.slice(1)
										: "Card"}{" "}
									· ···· {paymentMethodData.last4}
									{paymentMethodData.expMonth && paymentMethodData.expYear
										? ` · expires ${String(paymentMethodData.expMonth).padStart(2, "0")}/${paymentMethodData.expYear}`
										: ""}
								</span>
							) : paymentMethodData?.type === "sepa_debit" && paymentMethodData.last4 ? (
								<span>
									SEPA Direct Debit · IBAN ending in {paymentMethodData.last4}
									{paymentMethodData.sepaDebit?.country
										? ` (${paymentMethodData.sepaDebit.country})`
										: ""}
								</span>
							) : (
								<span>—</span>
							)}
							{isPaymentMethodError && !!data?.stripeSubscription && (
								<p className="text-xs text-text-meta mt-1">
									No usable payment method on file. Please add one via the billing portal.
								</p>
							)}
						</div>
						<div>
							<span className="text-gray-500">Next payment: </span>
							{data?.upcomingInvoice?.next_payment_attempt ? (
								<span>
									{new Intl.NumberFormat("de-DE", {
										style: "currency",
										currency: "EUR",
									}).format((data?.upcomingInvoice?.amount_due || 0) / 100)}{" "}
									on{" "}
									{format(new Date(data.upcomingInvoice.next_payment_attempt * 1000), "dd.MM.yyyy")}
								</span>
							) : (
								<span>—</span>
							)}
						</div>
					</div>
					<div className="mt-2">
						<Button
							color={ButtonColor.ACTION_SECONDARY}
							disabled={isCreatingPortalSession || !data?.stripeSubscription}
							isLoading={isCreatingPortalSession}
							title="Change payment details"
							onClick={handleOpenStripePortal}
						/>
					</div>
				</div>

				{/* Transaction History Section */}
				<div className="flex flex-col rounded-xl border border-gray-200 bg-brand-white p-5">
					<div className="text-sm font-semibold mb-3">Transaction history</div>
					<div className="mt-2">
						<Button
							color={ButtonColor.ACTION_SECONDARY}
							disabled={isCreatingPortalSession || !data?.stripeSubscription}
							isLoading={isCreatingPortalSession}
							title="See all invoices"
							onClick={handleOpenStripePortal}
						/>
					</div>
				</div>
			</div>

			{/* Right Column - FAQ */}
			<div>
				<div className="flex flex-col rounded-xl border border-gray-200 bg-brand-white p-5 sticky top-4">
					<div className="text-sm font-semibold mb-4">FAQ</div>
					<Accordion items={billingFaqItems} openItemId={openFaqItemId} />
				</div>
			</div>

			{/* Webscrape Credits Purchase Modal */}
			<SelectWebsiteScrapeProduct
				setShow={setShowWebsiteScrapeModal}
				show={showWebsiteScrapeModal}
			/>

			{/* Topup Credits Modal */}
			<TopupCreditsModal setShow={setShowTopupCreditsModal} show={showTopupCreditsModal} />

			{/* Edit Email Modal — mounted only while open so its form re-seeds from fresh slot data */}
			{slotsData && showEditEmailModal ? (
				<EditEmailModal
					baseAmount={slotsData.emailBaseAmount ?? 0}
					bonusAmount={slotsData.emailsBonusAmount ?? 0}
					isOpen={showEditEmailModal}
					paidAmount={slotsData.emailsPaidAmount ?? 0}
					pendingAmount={slotsData.emailsPendingAmount}
					pendingEffectiveAt={slotsData.emailsPendingEffectiveAt}
					setIsOpen={setShowEditEmailModal}
				/>
			) : null}

			{/* Edit LinkedIn Modal */}
			{slotsData && showEditLinkedinModal ? (
				<EditLinkedinModal
					baseAmount={slotsData.linkedinBaseAmount ?? 0}
					bonusAmount={slotsData.linkedinsBonusAmount ?? 0}
					isOpen={showEditLinkedinModal}
					paidAmount={slotsData.linkedinsPaidAmount ?? 0}
					pendingAmount={slotsData.linkedinsPendingAmount}
					pendingEffectiveAt={slotsData.linkedinsPendingEffectiveAt}
					setIsOpen={setShowEditLinkedinModal}
				/>
			) : null}

			{/* Change Subscription Modal */}
			<ChangeSubscriptionModal
				currentPriceId={data?.stripePrice?.id}
				isOpen={showChangeSubscriptionModal}
				setIsOpen={setShowChangeSubscriptionModal}
			/>

			{/* Skip Trial Confirmation Modal */}
			<Modal
				handleClose={() => setShowSkipTrialModal(false)}
				handleSave={handleSkipTrial}
				isLoading={isSkippingTrial}
				isOpened={showSkipTrialModal}
				submitButtonColor={ButtonColor.ACTION}
				submitButtonText="Skip Trial & Start Subscription"
				title="Skip Trial Period"
			>
				<div className="flex flex-col gap-3">
					<p className="text-sm">Are you sure you want to skip your trial period?</p>
					<div className="text-sm text-amber-700 bg-amber-50 border border-amber-200 rounded-md p-3">
						<strong>This will trigger an immediate payment</strong> for your subscription and you
						will receive the full credit amount included in your plan.
					</div>
				</div>
			</Modal>

			<CancelSubscriptionModal
				error={cancelError}
				expectedEnd={expectedCancellationEndInfo}
				isCancelling={isCancelling}
				isOpen={showCancelModal}
				isTrialing={isTrialing}
				onClose={() => {
					setShowCancelModal(false);
					setCancelError(null);
				}}
				onConfirm={handleCancelSubscription}
			/>

			{/* Reactivate Subscription Confirmation Modal */}
			<Modal
				handleClose={() => {
					setShowReactivateModal(false);
					setReactivateError(null);
				}}
				handleSave={handleReactivateSubscription}
				isLoading={isReactivating}
				isOpened={showReactivateModal}
				submitButtonColor={ButtonColor.ACTION}
				submitButtonText="Reactivate Subscription"
				title="Reactivate Subscription"
			>
				<div className="flex flex-col gap-3">
					<p className="text-sm">
						Are you sure you want to reactivate your subscription? Your plan will continue as normal
						and the scheduled cancellation will be removed.
					</p>
					{reactivateError && (
						<div className="text-sm text-red-700 bg-red-50 border border-red-200 rounded-md p-3">
							{reactivateError}
						</div>
					)}
				</div>
			</Modal>

			{/* Pending Credit Purchase Modal (async payment methods) */}
			<Modal
				cancelButtonText="Close"
				handleClose={() => setIsPendingPaymentOpen(false)}
				isOpened={isPendingPaymentOpen}
				title="Payment received — credits coming soon"
			>
				<div className="flex flex-col gap-3">
					<p className="text-sm text-text-meta">
						Your bank transfer / SEPA Direct Debit is being processed. Credits will be added to your
						account automatically once the payment clears (typically 1–5 business days). You can
						close this page.
					</p>
				</div>
			</Modal>

			{renderMessageSnackbar()}
		</div>
	);
};
